import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useTr } from "@/i18n/translator";
import { LifeBuoy, Check, ArrowLeft } from "lucide-react";

const CATS = [
  { id: "general", label: "General" },
  { id: "billing", label: "Facturación" },
  { id: "bug", label: "Bug / Error" },
  { id: "feature", label: "Mejora / Idea" },
];

export default function Support() {
  const tr = useTr();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", category: "general" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/support/tickets", form);
      setSent(data);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F0] text-[#0A0A0A]">
      <header className="border-b border-[#0A0A0A] bg-white px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 font-mono-mark text-[10px]" data-testid="support-back">
          <ArrowLeft className="w-4 h-4" /> CIA MARKETING® · {tr("VOLVER")}
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-0 py-12 md:py-20">
        <div className="flex items-center gap-3">
          <LifeBuoy className="w-10 h-10 text-[#E53935]" />
          <div>
            <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("CONTACTO · SOPORTE")}</div>
            <h1 className="font-display text-4xl sm:text-5xl">{tr("¿En qué podemos ayudarte?")}</h1>
          </div>
        </div>
        <p className="font-mono-data text-base text-[#5C5C5C] mt-4 max-w-2xl">
          {tr("Escríbenos y te responderemos por email. Normalmente en menos de 24 horas hábiles.")}
        </p>

        {sent ? (
          <div className="mt-10 border border-[#E53935] bg-[#FFD600] p-8" data-testid="support-sent">
            <Check className="w-10 h-10 text-[#E53935]" />
            <h2 className="font-display text-3xl mt-3">{tr("¡Mensaje recibido!")}</h2>
            <p className="font-mono-data text-sm mt-2">{sent.message}</p>
            <div className="font-mono-mark text-[10px] text-[#5C5C5C] mt-4">
              {tr("ID DE REFERENCIA")}: <span className="text-[#0A0A0A]">{sent.id}</span>
            </div>
            <Link to="/" className="inline-block mt-6 font-mono-mark text-[10px] bg-[#0A0A0A] text-white px-4 py-3 border border-[#0A0A0A]">
              {tr("VOLVER AL INICIO")}
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-5 bg-white border border-[#0A0A0A] p-6 md:p-8" data-testid="support-form">
            <div className="grid md:grid-cols-2 gap-4">
              <label className="block">
                <span className="font-mono-mark text-[10px] text-[#5C5C5C]">NOMBRE</span>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm"
                  data-testid="support-name"
                />
              </label>
              <label className="block">
                <span className="font-mono-mark text-[10px] text-[#5C5C5C]">EMAIL</span>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm"
                  data-testid="support-email"
                />
              </label>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <label className="block md:col-span-1">
                <span className="font-mono-mark text-[10px] text-[#5C5C5C]">CATEGORÍA</span>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm"
                  data-testid="support-category"
                >
                  {CATS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </label>
              <label className="block md:col-span-1">
                <span className="font-mono-mark text-[10px] text-[#5C5C5C]">ASUNTO</span>
                <input
                  required
                  minLength={3}
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm"
                  data-testid="support-subject"
                />
              </label>
            </div>

            <label className="block">
              <span className="font-mono-mark text-[10px] text-[#5C5C5C]">MENSAJE</span>
              <textarea
                required
                minLength={5}
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm"
                placeholder={tr("Cuéntanos qué necesitas…")}
                data-testid="support-message"
              />
            </label>

            {err && (
              <div className="border border-[#E53935] bg-[#E53935]/10 px-4 py-3 font-mono-data text-sm text-[#E53935]" data-testid="support-error">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full md:w-auto bg-[#E53935] text-white px-8 py-3 font-mono-mark text-xs border border-[#E53935] hover-brutal disabled:opacity-50"
              data-testid="support-submit"
            >
              {loading ? "ENVIANDO…" : "ENVIAR MENSAJE"}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
